import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../api';
import { MANUAL_KEY } from './HintsProvider';
import { firstHeading, Markdown } from './markdown';

export type ManualSection = { featureId: string; anchor: string; hint: string; body: string };
export type ManualModule = { module: string; title: string; sections: ManualSection[] };

/** A hand-typed or truncated link (`#%E0`) must not crash the page: an undecodable hash means "no target". */
function anchorFromHash(hash: string): string {
  try {
    return decodeURIComponent(hash.replace(/^#/, ''));
  } catch {
    return '';
  }
}

export function ManualPage() {
  const manual = useQuery({ queryKey: MANUAL_KEY, queryFn: () => api<{ modules: ManualModule[] }>('/api/hints/manual'), staleTime: 60_000 });
  const { hash } = useLocation();
  const target = anchorFromHash(hash);

  useEffect(() => {
    if (!target || !manual.data) return;
    document.getElementById(target)?.scrollIntoView({ block: 'start' });
  }, [target, manual.data]);

  return (
    <div className="page manual">
      <div className="page-head">
        <h1>Instrukcja</h1>
      </div>
      {manual.isLoading && (
        <p className="muted" role="status">
          Wczytywanie…
        </p>
      )}
      {manual.error && (
        <p className="alert" role="alert">
          Nie udało się wczytać instrukcji. Odśwież stronę.
        </p>
      )}
      {manual.data && (
        <>
          <nav className="manual-toc" aria-label="Spis treści">
            {manual.data.modules.map((m) => (
              <div key={m.module}>
                <h2>{m.title}</h2>
                <ul>
                  {m.sections.map((s) => (
                    <li key={s.featureId}>
                      <Link to={`#${s.anchor}`}>{titleOf(s)}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
          {manual.data.modules.map((m) => (
            <div key={m.module} className="manual-module">
              <h2 className="manual-module-title">{m.title}</h2>
              {m.sections.map((s) => (
                <section key={s.featureId} id={s.anchor} className={`panel manual-section${s.anchor === target ? ' is-target' : ''}`}>
                  <Markdown text={s.body} />
                </section>
              ))}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

const titleOf = (s: ManualSection): string => firstHeading(s.body) ?? s.featureId;
